import { useState } from "react";
import {
  ChevronUp,
  ChevronDown,
  Eye,
  Edit,
  Trash2,
  MoreHorizontal,
} from "lucide-react";
import Pagination from "./pagination";
import { Link } from "react-router-dom";
import DeleteConfirmationModal from "../../projects/components/DeleteConfirmationModal";

export default function ObservationTable({
  observations,
  isLoading,
  sortField,
  sortDirection,
  onSort,
  currentPage,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange,
  handleDelete,
}) {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [observationToDelete, setObservationToDelete] = useState(null);

  const toggleDropdown = (id) => {
    if (activeDropdown === id) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(id);
    }
  };

  const handleClickOutside = () => {
    setActiveDropdown(null);
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return null;

    return sortDirection === "asc" ? (
      <ChevronUp className="h-4 w-4 inline-block ml-1" />
    ) : (
      <ChevronDown className="h-4 w-4 inline-block ml-1" />
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "latest":
        return "bg-red-100 text-red-800";
      case "under":
        return "bg-yellow-100 text-yellow-800";
      case "closed":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "latest":
        return "Needs Action";
      case "under":
        return "Under Control";
      case "closed":
        return "Completed";
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (observations.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-md">
        <p className="text-gray-500 text-lg">No observations found</p>
      </div>
    );
  }

  const confirmDelete = (project) => {
    setObservationToDelete(project);
    setModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (observationToDelete) {
      handleDelete(observationToDelete._id);
      setModalOpen(false);
      setObservationToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setModalOpen(false);
    setObservationToDelete(null);
  };

  return (
    <div className="overflow-x-auto" onClick={handleClickOutside}>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
              onClick={() => onSort("projectDetail.projectId.projectName")}
            >
              <div className="flex items-center">
                Project
                {renderSortIcon("projectDetail.projectId.projectName")}
              </div>
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
              onClick={() => onSort("projectDetail.userId.name")}
            >
              <div className="flex items-center">
                CreatedBy
                {renderSortIcon("projectDetail.userId.name")}
              </div>
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
              onClick={() => onSort("observerDetails.observerDetail")}
            >
              <div className="flex items-center">
                Observer
                {renderSortIcon("observerDetails.observerDetail")}
              </div>
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
              onClick={() => onSort("projectDetail.dateOfObservation")}
            >
              <div className="flex items-center">
                Date
                {renderSortIcon("projectDetail.dateOfObservation")}
              </div>
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
              onClick={() => onSort("status")}
            >
              <div className="flex items-center">
                Status
                {renderSortIcon("status")}
              </div>
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
              onClick={() => onSort("updatedAt")}
            >
              <div className="flex items-center">
                Last Updated
                {renderSortIcon("updatedAt")}
              </div>
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {observations.map((observation) => (
            <tr
              key={observation._id}
              className={`hover:bg-gray-50 ${
                observation.status === "latest"
                  ? "bg-red-50"
                  : observation.status === "under"
                  ? "bg-yellow-50"
                  : observation.status === "closed"
                  ? "bg-green-50"
                  : ""
              }`}
            >
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">
                  {observation.projectId.projectName}
                </div>
                <div className="text-sm text-gray-500">
                  {observation.projectId?.projectNumber}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">
                  {observation?.userId?.name}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  {observation.observerDetails.observerDetail}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  {formatDate(observation.projectDetail.dateOfObservation)}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClass(
                    observation.status
                  )}`}
                >
                  {getStatusText(observation.status)}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatDate(observation.updatedAt)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium relative">
                <div className="flex items-center space-x-2">
                  <Link
                    to={`/admin/observations/observation-detail-page/${observation?._id}`}
                  >
                    <button
                      className="text-blue-600 hover:text-blue-900"
                      aria-label="View observation"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                  </Link>

                  <Link
                    to={`/admin/observations/observation-edit-page/${observation?.projectId?._id}/${observation?._id}`}
                  >
                    <button
                      className="text-amber-600 hover:text-amber-900"
                      aria-label="Edit observation"
                    >
                      <Edit className="h-5 w-5" />
                    </button>
                  </Link>

                  <button
                    onClick={() => confirmDelete(observation)}
                    className="text-red-600 hover:text-red-900"
                    aria-label="Delete observation"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleDropdown(observation._id);
                    }}
                    className="text-gray-600 hover:text-gray-900 md:hidden"
                    aria-label="More options"
                  >
                    <MoreHorizontal className="h-5 w-5" />
                  </button>

                  {activeDropdown === observation._id && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10 top-8">
                      <Link
                        to={`/admin/observations/observation-detail-page/${observation?._id}`}
                      >
                        <button className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                          View Details
                        </button>
                      </Link>

                      <Link
                        to={`/admin/observations/observation-edit-page/${observation.projectId}/${observation._id}`}
                      >
                        <button className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                          Edit Observation
                        </button>
                      </Link>

                      <button
                        onClick={() => confirmDelete(observation)}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                      >
                        Delete Observation
                      </button>
                    </div>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Pagination
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={onPageChange}
        onItemsPerPageChange={onItemsPerPageChange}
      />

      <DeleteConfirmationModal
        isOpen={modalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        projectName={observationToDelete?.observerDetails?.observerDetail}
      />
    </div>
  );
}
